/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import i18n from "../i18n/index.js";

const t = i18n.global.t;

/**
 * Represents a custom error type with additional properties for error code and cause.
 *
 * This class extends the built-in JavaScript `Error` class, allowing users to include
 * an error code and a cause for more descriptive error handling.
 */
class PristyError extends Error {
  constructor(message, code, cause) {
    super(message);
    this.code = code;
    this.cause = cause;
  }
}

/**
 * Translates an error message based on the provided prefix and cause.
 * Attempts to find a localized error message using the prefix and status code of the cause,
 * falling back to less specific translations or the original message if necessary.
 *
 * @param {string} prefix - The prefix used to construct the translation key for the error message.
 * @param {Object} cause - The cause of the error, containing the `status` and `message` properties.
 * @param {number|string} cause.status - The status code representing the error.
 * @param {string} cause.message - The original error message.
 * @return {string} The translated error message or the original error message if translation is not available.
 */
function translateErrorMessage(prefix, cause) {
  let message = t("error." + prefix + "." + cause.status);
  if (!message) {
    message = t("error." + cause.status);
  }
  if (!message) {
    message = cause.message;
  }
  return message;
}

/**
 * Creates a new instance of PristyError with a customized message based on the error details.
 *
 * @param {string} prefix - The prefix to use for translating or constructing error messages.
 * @param {Object} error - The error object containing information about the error.
 * @param {number} error.status - The HTTP status code of the error.
 * @param {Object} [error.response] - An optional response object containing additional error details.
 * @param {Object} [error.response.error] - An optional nested error detail object.
 * @param {string} [error.response.error.message] - A specific message detailing the error.
 * @param {string|Object} error.message - The raw message of the error, which might be in JSON format.
 * @return {PristyError} A new PristyError instance with a constructed or translated message and additional error details.
 */
export function newPristyError(prefix, error) {
  let message;
  if (error.status === 400) {
    let parsedMessage;
    if (error.response?.error?.message) {
      throw new PristyError(
        error.response?.error?.message,
        error.status,
        error,
      );
    }

    try {
      parsedMessage =
        typeof error.message === "string"
          ? JSON.parse(error.message)
          : error.message;
    } catch (error) {
      console.error("Erreur de parsing JSON :", error);
      parsedMessage = {};
    }

    message =
      parsedMessage?.error?.briefSummary ||
      translateErrorMessage(prefix, error);
  } else {
    message = translateErrorMessage(prefix, error);
  }

  return new PristyError(message, error.status, error);
}

/**
 * A service class responsible for handling various error scenarios related to operations such as updating a node,
 * uploading files, deleting nodes, and retrieving upload error details.
 *
 * TODO rename to TranslateErrorService
 */
class ErrorService {
  updateNodeError(error) {
    if (error.status === 409) {
      let parsedMessage;
      try {
        parsedMessage =
          typeof error.message === "string"
            ? JSON.parse(error.message)
            : error.message;
      } catch (e) {
        console.error("Erreur de parsing JSON :", e);
        parsedMessage = {};
      }

      // Si parsedMessage est un objet, vérifie si briefSummary contient les mots
      const messageToCheck = parsedMessage?.error?.briefSummary || "";

      if (
        messageToCheck.includes("verrouillé") ||
        messageToCheck.includes("locked")
      ) {
        throw newPristyError(t("updateNodeLocked"), error);
      }
    }
    throw newPristyError("updateNode", error);
  }

  uploadFileError(error, filename) {
    if (error.status === 409) {
      throw new PristyError(
        t("error.uploadFile.409", { filename: filename }),
        error.status,
        error,
      );
    }

    throw newPristyError("uploadFile", error);
  }

  deleteNodeError(error, type) {
    switch (error.status) {
      case 403:
      case 404:
      case 409:
        throw new PristyError(
          t(`error.deleteNode.${error.status}`, { type: t(type) }),
          error.status,
          error,
        );

      default:
        throw newPristyError("deleteNode", error);
    }
  }

  getUploadErrors(listError) {
    let errors = [];
    listError.forEach((error) => {
      errors.push({
        severity: "error",
        content: t("uploadUnknownError", {
          error: error.message,
        }),
      });
    });
    return errors.filter((error, index) => {
      return (
        index ===
        errors.findIndex((otherError) => {
          return error.content === otherError.content;
        })
      );
    });
  }
}

export function loginError(data) {
  let caughtError;
  if (data.error) {
    if (data.error.response) {
      caughtError = data.error.response;
    } else if (data.error.message) {
      caughtError = data.error.message;
    }
  } else if (data.message) {
    try {
      caughtError = JSON.parse(data.message).error;
    } catch (e) {
      console.error("Login error", e);
      caughtError = data.message;
    }
  } else {
    caughtError = data;
  }
  if (caughtError.statusCode) {
    // Try to translate Code
    switch (caughtError.statusCode) {
      case 400:
        caughtError.message = t("error.login.400");
        break;
      case 401:
        caughtError.message = t("error.login.401");
        break;
      case 403:
        caughtError.message = t("error.login.403");
        break;
      case 502:
        caughtError.message = t("alfrescoError");
        break;
      default:
        caughtError.message = caughtError.statusText
          ? caughtError.statusText
          : caughtError.message;
    }
  }

  return caughtError;
}

export default new ErrorService();
